/**
 * Signaling over the PHP endpoints: POST to write, poll to read.
 *
 * Polling is the right shape here rather than a concession: the exchange is a
 * handful of messages over a couple of seconds, after which the data channel
 * carries everything and this client goes quiet. There is no server to hold a
 * socket open on shared hosting anyway.
 */
import {
  type SignalHandler,
  type SignalMessage,
  type Signaling,
  SignalHandlers,
  addressedTo,
  makePeerId,
} from './signaling.js';

export interface HttpSignalingOptions {
  /** Endpoint base. `/api` in production, proxied to PHP in dev. */
  base?: string;
  peerId?: string;
  /** Poll period while things are healthy. */
  pollMs?: number;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

interface PollResponse {
  messages: {
    id: number;
    from: string;
    to: string | null;
    kind: SignalMessage['kind'];
    payload?: unknown;
  }[];
  latest: number;
}

const MAX_BACKOFF_MS = 5000;

export class HttpSignaling implements Signaling {
  readonly room: string;
  readonly peerId: string;

  private readonly base: string;
  private readonly pollMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly handlers = new SignalHandlers();

  private since = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private backoff = 0;

  constructor(room: string, opts: HttpSignalingOptions = {}) {
    this.room = room;
    this.peerId = opts.peerId ?? makePeerId();
    this.base = (opts.base ?? '/api').replace(/\/$/, '');
    this.pollMs = opts.pollMs ?? 600;
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Ask the server for a room code. Only the host does this. */
  static async createRoom(
    base = '/api',
    fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
  ): Promise<string> {
    const res = await fetchImpl(`${base.replace(/\/$/, '')}/room`, { method: 'POST' });
    if (!res.ok) throw new Error(`could not create a room (${res.status})`);
    const body = (await res.json()) as { room?: string };
    if (!body.room) throw new Error('server returned no room code');
    return body.room;
  }

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    // Sync the cursor first so a joiner does not replay the room's backlog and
    // try to answer offers that were meant for someone else.
    try {
      const first = await this.poll();
      this.since = first.latest;
    } catch {
      // The first poll failing is not fatal; the loop retries with backoff.
    }
    this.schedule(0);
  }

  private schedule(delay: number): void {
    if (!this.running) return;
    this.timer = setTimeout(() => void this.tick(), delay);
  }

  private async tick(): Promise<void> {
    if (!this.running) return;
    try {
      const body = await this.poll(this.since);
      this.since = Math.max(this.since, body.latest);
      this.backoff = 0;
      for (const row of body.messages) {
        const message: SignalMessage = {
          from: row.from,
          to: row.to,
          kind: row.kind,
          ...(row.payload === undefined || row.payload === null ? {} : { payload: row.payload }),
        };
        if (addressedTo(message, this.peerId)) this.handlers.emit(message);
      }
      this.schedule(this.pollMs);
    } catch {
      // A flaky poll should slow down, not spin.
      this.backoff = Math.min(MAX_BACKOFF_MS, this.backoff ? this.backoff * 2 : this.pollMs);
      this.schedule(this.backoff);
    }
  }

  private async poll(since = 0): Promise<PollResponse> {
    const url = `${this.base}/signal?room=${encodeURIComponent(this.room)}&since=${since}`;
    const res = await this.fetchImpl(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`poll failed (${res.status})`);
    const body = (await res.json()) as Partial<PollResponse>;
    return { messages: body.messages ?? [], latest: body.latest ?? since };
  }

  async send(message: Omit<SignalMessage, 'from'>): Promise<void> {
    const body = JSON.stringify({
      room: this.room,
      from: this.peerId,
      to: message.to,
      kind: message.kind,
      payload: message.payload ?? null,
    });
    try {
      await this.fetchImpl(`${this.base}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch {
      // Losing one candidate is survivable: ICE sends plenty of them.
    }
  }

  onMessage(handler: SignalHandler): () => void {
    return this.handlers.add(handler);
  }

  close(): void {
    // keepalive lets this land even if the tab is closing.
    void this.send({ to: null, kind: 'leave' });
    this.running = false;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.handlers.clear();
  }
}
